import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import React, { ReactElement, useRef, useState } from 'react';
import { cn } from 'src/domains/common/components/utils';
import { observer } from 'src/utils/mobx-react';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { ClickOutsideHandler } from 'src/domains/common/components/clickOutside';
import { AutoPosition } from 'src/domains/common/components/autoPosition';
import { ValueType, SelectState, ValueWithOnClickType } from './select.state';

interface SelectPropsType<T> {
    /* className to style option dropdown */
    className?: string;
    description?: string;
    formInputState: FormInputState<T, unknown>;
    /* Set position fixed and automatically positioning dropdown list */
    isAutoPosition?: boolean;
    label?: React.ReactNode;
    placeholder: string;
    /* triggerClassName to style trigger button */
    triggerClassName?: string;
    values: Array<ValueType<T>> | Array<ValueWithOnClickType<T>>;
    errors: string[];
    disabled?: boolean;
    dataTest?: string;
    canBeCleared?: boolean;
}

export const Select = observer(
    'Select',
    <T,>({
        className,
        description,
        formInputState,
        isAutoPosition = false,
        label,
        placeholder,
        triggerClassName,
        values,
        errors,
        disabled,
        dataTest,
        canBeCleared = false,
    }: SelectPropsType<T>): ReactElement => {
        const [selectState] = useState(() => new SelectState(formInputState, values, placeholder));
        const { expanded, setExpanded, setValue, selectValues, unsetValue } = selectState;
        const triggerRef = useRef<HTMLDivElement>(null);
        const error = errors.length === 0 ? null : (errors[0] ?? null);
        const isDisabled = disabled === true;
        const triggerLabel = selectValues.find((item) => item.value === formInputState.value)?.label ?? placeholder; // TODO - move to the state ~ TO

        const isValueWithOnclick = (value: ValueType<T>): value is ValueWithOnClickType<T> => {
            return 'onClick' in value && typeof value.onClick === 'function';
        };

        const renderOptions = (): ReactElement[] =>
            values.map((item) => (
                <div
                    key={JSON.stringify(item.value)}
                    data-test={item.dataTest}
                    className={cn(
                        'relative flex cursor-default select-none rounded-sm py-2 pl-2 pr-8 mx-1 text-sm outline-none focus:bg-accent focus:text-accent-foreground hover:bg-secondary',
                        item.description === undefined ? 'items-center' : 'flex-col items-start gap-0.5',
                        item.disabled === true && 'opacity-50 pointer-events-none'
                    )}
                    onClick={async () => {
                        if (isValueWithOnclick(item)) {
                            const proceed = await item.onClick();

                            if (proceed === false) return;
                        }

                        if (canBeCleared && item.value === formInputState.value) {
                            unsetValue();
                        } else {
                            setValue(item.value, item.disabled ?? false);
                        }
                    }}
                >
                    <span className='font-medium'>{item.label}</span>
                    {item.description === undefined ? null : (
                        <span className='text-xs text-muted-foreground'>{item.description}</span>
                    )}
                    {item.value === formInputState.value && (
                        <span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
                            <CheckIcon className='w-4 h-4' />
                        </span>
                    )}
                </div>
            ));

        return (
            <ClickOutsideHandler onClickOutside={() => setExpanded(false)}>
                {label !== undefined && <span className='block mb-2 text-sm'>{label}</span>}
                <div
                    ref={triggerRef}
                    onClick={() => setExpanded(expanded === false)}
                    data-test={dataTest}
                    className={cn(
                        'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input border-solid bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 relative',
                        error === null ? '' : 'border-destructive',
                        isDisabled === false ? '' : 'cursor-not-allowed opacity-50 pointer-events-none',
                        triggerClassName
                    )}
                >
                    <span className={cn(formInputState.value === '' && 'text-muted-foreground')}>{triggerLabel}</span>
                    <ChevronDownIcon className={cn('w-4 h-4 opacity-50', expanded && 'rotate-180')} />

                    {values.length > 0 &&
                        expanded &&
                        (isAutoPosition === true ? (
                            <AutoPosition
                                triggerRef={triggerRef}
                                className={cn(
                                    'fixed mt-1 mb-1 z-50 flex flex-col w-[var(--trigger-width)] max-h-96 min-w-[8rem] overflow-auto rounded-md border border-solid border-border bg-popover text-popover-foreground shadow-md transition-opacity duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out py-1',
                                    className
                                )}
                            >
                                {renderOptions()}
                            </AutoPosition>
                        ) : (
                            <div
                                className={cn(
                                    'absolute top-full left-0 mt-1 mb-1 z-50 flex flex-col w-full max-h-96 min-w-[8rem] overflow-auto rounded-md border border-solid border-border bg-popover text-popover-foreground shadow-md transition-opacity duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out py-1',
                                    className
                                )}
                            >
                                {renderOptions()}
                            </div>
                        ))}
                </div>

                {description !== undefined && error === null && (
                    <span className='block mt-2 text-sm text-muted-foreground'>{description}</span>
                )}
                {error === null ? null : <span className='text-destructive text-sm block mt-2'>{error}</span>}
            </ClickOutsideHandler>
        );
    }
);
